import React, { Component } from 'react';
import ReactDOM from "react-dom";
import { connect } from 'react-redux';
import server from '../../../api/server'; // Import the server instance

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle, faSyncAlt, faServer, faHashtag, faSearch, faTimes, faSpinner, faChevronDown, faAngleDown } from '@fortawesome/free-solid-svg-icons';

import InputTitle from './InputTitle';
import RefreshBotData from '../elements/RefreshBotData'; // Keep if refresh functionality is needed, maybe move near InputTitle

export class ChannelSelect extends Component {
    constructor(props) {
        super(props);

        this.state = {
            showDropDown: false,
            search_term: "",
            selectionMode: 'default', // default, guildSelect, guild, idInput
            selectedGuildId: null,
            guildsList: [],
            guildsLoading: false,
            guildsError: null,
            // No pagination for now, fetch all guilds
            // hasMoreGuilds: true,
            // guildsLoadingMore: false,
            guildContentData: { channels: [] }, // Store fetched channels { channels: [] }
            guildContentLoading: false,
            guildContentError: null,
            selectedChannel: null, // Store the selected channel object {id, name, type?}
            idToAdd: '' // For adding channel by ID
        };
    }

    componentDidMount() {
        document.addEventListener("click", this.handleClick, true);
        this.parseInitialValue();
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.handleClick, true);
    }

    componentDidUpdate(prevProps) {
        // If the external value prop changes, update the internal state
        if (this.props.value !== prevProps.value) {
            this.parseInitialValue();
        }
    }

    parseInitialValue = () => {
        try {
            let initialValue = null;
            const rawValue = this.props.value;

            if (rawValue) {
                if (typeof rawValue === 'string') {
                    // Try parsing if it's a string
                    initialValue = JSON.parse(rawValue);
                } else if (typeof rawValue === 'object' && rawValue !== null) {
                    // Use directly if it's already an object (and not null)
                    initialValue = rawValue;
                }
            }

            // Validate the structure slightly
            if (initialValue && initialValue.id && initialValue.name) {
                // Only update state if the value is different to avoid loops
                // Stringify for comparison might be needed if objects have different references but same content
                const currentSelectedString = this.state.selectedChannel ? JSON.stringify(this.state.selectedChannel) : null;
                const newSelectedString = JSON.stringify(initialValue);

                if (currentSelectedString !== newSelectedString) {
                    this.setState({ selectedChannel: initialValue });
                }
            } else {
                // Reset if value is invalid, null, or not the expected shape
                if (this.state.selectedChannel !== null) { // Only setState if it needs changing
                    this.setState({ selectedChannel: null });
                }
            }
        } catch (error) {
            console.error("ChannelSelect: Error processing initial value prop:", error, "Value:", this.props.value);
            if (this.state.selectedChannel !== null) { // Only setState if it needs changing
                this.setState({ selectedChannel: null }); // Reset on error
            }
        }
    };

    handleClick = (event) => {
        const domNode = ReactDOM.findDOMNode(this);
        if (!domNode || !domNode.contains(event.target)) {
            this.setState({
                showDropDown: false,
                selectionMode: 'default', // Reset mode on close
                // Keep selectedGuildId potentially, but clear search?
                search_term: ""
            });
        }
    };

    handleChannelSelect = (channel) => {
        const channelObject = {
            id: channel.id,
            name: channel.name,
            type: channel.type // Include type if available and needed
        };
        this.setState({
            selectedChannel: channelObject,
            showDropDown: false,
            selectionMode: 'default',
            selectedGuildId: null, // Reset guild context after selection
            guildContentData: { channels: [] }, // Clear guild content
            guildContentLoading: false,
            guildContentError: null,
            search_term: "" // Clear search
        });
        // Call the parent's change handler with the object
        this.props.change(channelObject);
    };


    selectGuild = (guildId) => {
        this.setState({
            selectionMode: 'guildSelect',
            selectedGuildId: guildId,
            guildContentLoading: false, // Reset loading states
            guildContentError: null,
            guildContentData: { channels: [] }, // Clear previous data
            search_term: '' // Reset search term
        }, () => {
            this.fetchGuildContent(guildId);
        });
    };

    resetSelectionMode = () => {
        this.setState({
            selectionMode: 'default',
            selectedGuildId: null,
            search_term: "",
            idToAdd: '' // Reset ID input on mode change
        });
    };

    // State transition handler for selecting "Select from Server"
    switchToGuild = () => {
        this.setState({
            selectionMode: 'guild',
            selectedGuildId: null,
            search_term: '',
            guildsList: [], // Clear list on switch
        }, () => {
            this.fetchGuilds(); // Fetch guilds when switching to this mode
        });
    };

    fetchGuilds = async () => {
        if (!this.props.bot || !this.props.bot.id) {
            this.setState({ guildsError: "Bot information is missing.", guildsLoading: false });
            return;
        }

        this.setState({ guildsLoading: true, guildsError: null });

        try {
            // Fetch all guilds, no pagination for now
            const response = await server.post("/dashboard/getGuilds", { id: this.props.bot.id });

            if (response.data && response.data.success) {
                const newGuilds = response.data.guilds || [];
                const sortedGuilds = newGuilds.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                this.setState({
                    guildsList: sortedGuilds,
                    guildsLoading: false,
                    guildsError: null
                });
            } else {
                const errorMessage = response.data.reason ? `Failed to fetch guilds: ${response.data.reason}` : "Failed to fetch guilds.";
                this.setState({ guildsError: errorMessage, guildsLoading: false });
            }
        } catch (error) {
            console.error("ChannelSelect: fetchGuilds API error:", error);
            this.setState({ guildsError: "An error occurred while fetching guilds.", guildsLoading: false });
        }
    };


    // Fetch channels for a selected guild
    fetchGuildContent = async (guildId) => {
        if (!this.props.bot || !this.props.bot.id || !guildId) {
            this.setState({ guildContentError: "Missing bot or guild information.", guildContentLoading: false });
            return;
        }

        this.setState({ guildContentLoading: true, guildContentError: null, guildContentData: { channels: [] } }); // Clear previous channel data

        try {
            const response = await server.post("/dashboard/guildData", {
                id: this.props.bot.id, // Bot ID
                guildId: guildId,
                dataType: 'channels' // Explicitly request channels
            });

            if (response.data && response.data.success) {
                // Filter channels based on props.settings.channel_filter immediately after fetch
                const allChannels = response.data.data || [];
                const channelFilter = this.props.settings?.channel_filter;

                const filteredChannels = allChannels.filter(channel => {
                    // Default behavior: Include Text (0) and Category (4) if no filter
                    if (!channelFilter || channelFilter.length === 0) {
                        return channel.type === 0 || channel.type === 4;
                    }
                    // If filter exists, check if channel type is included
                    return channelFilter.includes(channel.type);
                });


                this.setState({
                    guildContentData: { channels: filteredChannels },
                    guildContentLoading: false,
                    guildContentError: null
                });
            } else {
                const errorMessage = response.data.reason ? `Failed to load channels: ${response.data.reason}` : `Failed to load channels.`;
                this.setState({ guildContentError: errorMessage, guildContentLoading: false });
            }
        } catch (error) {
            console.error(`Error fetching channels for guild ${guildId}:`, error);
            this.setState({ guildContentError: `An error occurred while fetching channels.`, guildContentLoading: false });
        }
    };

    // --- New methods for Add by ID ---
    switchToIdInput = () => {
        this.setState({
            selectionMode: 'idInput',
            selectedGuildId: null // Ensure guild selection context is cleared
        });
    };

    handleChannelAddById = () => {
        const idToAdd = this.state.idToAdd.trim();
        if (!idToAdd) return;

        // Create a basic channel object
        // We don't know the real name or type when adding by ID only
        const channelObject = {
            id: idToAdd,
            name: `Channel: ${idToAdd}`,
            // type: undefined // Or a default type if necessary, but probably best to omit
        };

        this.setState({
            selectedChannel: channelObject,
            showDropDown: false,
            selectionMode: 'default',
            idToAdd: '', // Clear the input field
            // Reset other potentially relevant state
            selectedGuildId: null,
            guildContentData: { channels: [] },
            guildContentLoading: false,
            guildContentError: null,
            search_term: ""
        });

        // Call the parent's change handler with the object
        this.props.change(channelObject);
    };

    // --- Rendering Methods ---

    renderDescription = () => {
        if (this.props.settings.html) {
            return <div dangerouslySetInnerHTML={{ __html: this.props.settings.description }}></div>;
        } else {
            return <p className="text-sm text-[#b9bbbe]">{this.props.settings.description}</p>;
        }
    };

    renderGuildList = () => {
        const { guildsLoading, guildsError, guildsList, search_term } = this.state;

        const renderGuildIcon = (guild) => {
            if (guild.icon) {
                return (
                    <img
                        src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png?size=32`}
                        alt={`${guild.name} icon`}
                        className="w-6 h-6 rounded-full mr-2 flex-shrink-0"
                        onError={(e) => { e.target.src = ''; e.target.style.display = 'none'; /* Hide if image fails */ }}
                    />
                );
            } else {
                const initial = guild.name ? guild.name.charAt(0).toUpperCase() : '?';
                return (
                    <div className="w-6 h-6 rounded-full mr-2 flex-shrink-0 bg-[#5865f2] flex items-center justify-center text-white text-xs font-bold">
                        {initial}
                    </div>
                );
            }
        };

        let listContent;
        if (guildsList.length === 0 && !guildsLoading) {
            listContent = (
                <div className="p-4 text-center text-[#b9bbbe]">
                    {guildsError ? `Error: ${guildsError}` : 'No servers found for this bot.'}
                    {guildsError && (
                        <button
                            onClick={this.fetchGuilds}
                            className="ml-2 px-2 py-1 bg-[#5865f2] text-white text-xs rounded hover:bg-[#4752c4]"
                        >
                            Retry
                        </button>
                    )}
                </div>
            );
        } else {
            const filteredGuilds = guildsList.filter(guild =>
                guild.name.toLowerCase().includes(search_term.toLowerCase()) ||
                guild.id.includes(search_term)
            );

            listContent = (
                <div className="guild-items-list py-1 max-h-60 overflow-y-auto custom-scrollbar">
                    {filteredGuilds.length === 0 && search_term !== "" && (
                        <div className="p-2 mx-2 text-center text-[#b9bbbe]">No matching servers found.</div>
                    )}
                    {filteredGuilds.map((guild) => (
                        <div
                            key={guild.id}
                            className="p-2 mx-2 my-1 hover:bg-[#3a3d42] cursor-pointer flex items-center text-[#b9bbbe] rounded"
                            onClick={() => this.selectGuild(guild.id)}
                        >
                            {renderGuildIcon(guild)}
                            <span className="text-[#ffffff] truncate" title={`${guild.name} (${guild.id})`}>{guild.name}</span>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="guild-list-container flex flex-col h-full">
                <div className="flex items-center justify-between p-3 border-b border-[#1a1c1f]">
                    <h3 className="text-lg font-semibold text-[#ffffff]">Select a Server</h3>
                    <FontAwesomeIcon
                        icon={faTimes}
                        className="cursor-pointer text-[#b9bbbe] hover:text-white"
                        onClick={this.resetSelectionMode} // Go back to default options
                    />
                </div>
                <div className="p-3 border-b border-[#1a1c1f]">
                    <input
                        type="text"
                        className="w-full bg-[#202225] border border-[#1a1c1f] rounded px-3 py-2 text-[#dcddde] placeholder-[#72767d] focus:outline-none focus:border-[#ff4444]"
                        placeholder="Search servers by name or ID..."
                        value={this.state.search_term}
                        onChange={(e) => this.setState({ search_term: e.target.value })}
                        disabled={guildsLoading && guildsList.length === 0}
                    />
                </div>

                {guildsLoading && guildsList.length === 0 && (
                    <div className="p-4 text-center text-[#b9bbbe] flex items-center justify-center">
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> Loading servers...
                    </div>
                )}

                {!guildsLoading && (
                    <div className="flex-grow overflow-hidden">
                        {listContent}
                    </div>
                )}
            </div>
        );
    };

    renderGuildContent = () => { // Renamed from RoleAdd's version
        const { selectedGuildId, guildContentLoading, guildContentError, guildContentData, search_term } = this.state;
        const dataType = 'channels'; // Hardcoded for this component

        if (!selectedGuildId) return null;

        const guildName = this.state.guildsList.find(g => g.id === selectedGuildId)?.name || selectedGuildId;
        const title = `${guildName} - Channels`;
        const searchPlaceholder = `Search channels...`;

        let content;
        if (guildContentLoading) {
            content = <div className="p-4 text-center text-[#b9bbbe]"><FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> Loading channels...</div>;
        } else if (guildContentError) {
            content = (
                <div className="p-4 text-center">
                    <span style={{ color: '#F87171' }}>Error: {guildContentError}</span>
                    <button
                        onClick={() => this.fetchGuildContent(selectedGuildId)}
                        className="ml-2 px-2 py-1 bg-[#5865f2] text-white text-xs rounded hover:bg-[#4752c4]"
                    >
                        Retry
                    </button>
                </div>
            );
        } else {
            const channelsToRender = (guildContentData[dataType] || []).filter(channel =>
                channel.name.toLowerCase().includes(search_term.toLowerCase())
            );

            // Channel filter already applied during fetchGuildContent

            content = (
                <div className="max-h-60 overflow-y-auto guild-items-list py-1 custom-scrollbar">
                    {channelsToRender.length === 0 && (
                        <div className="p-2 mx-2 text-center text-[#b9bbbe]">No matching channels found{this.props.settings?.channel_filter ? ' (matching filter)' : ''}.</div>
                    )}
                    {channelsToRender.map((channel) => (
                        <div
                            key={channel.id}
                            className="p-2 mx-2 my-1 hover:bg-[#3a3d42] cursor-pointer flex items-center text-[#b9bbbe] rounded"
                            onClick={() => this.handleChannelSelect(channel)} // Use the selection handler
                        >
                            <FontAwesomeIcon icon={faHashtag} className="mr-2 text-[#ffffff]" />
                            <span className="text-[#ffffff] truncate">{channel.name}</span>
                        </div>
                    ))}
                </div>
            );

        }

        return (
            <div className="guild-content-container">
                <div className="flex items-center justify-between p-3 border-b border-[#1a1c1f]">
                    {/* Back button */}
                    <button
                        onClick={() => this.setState({ selectionMode: 'guild', search_term: '' })} // Go back to guild list
                        className="text-[#b9bbbe] hover:text-white mr-2"
                        aria-label="Back to server list"
                    >
                        &lt; Back
                    </button>
                    <h3 className="text-lg font-semibold text-[#ffffff] truncate flex-grow text-center" title={title}>{title}</h3>
                    <FontAwesomeIcon
                        icon={faTimes}
                        className="cursor-pointer text-[#b9bbbe] hover:text-white"
                        onClick={this.resetSelectionMode} // Close dropdown entirely
                    />
                </div>
                <div className="p-3 border-b border-[#1a1c1f]">
                    <input
                        type="text"
                        className="w-full bg-[#202225] border border-[#1a1c1f] rounded px-3 py-2 text-[#dcddde] placeholder-[#72767d] focus:outline-none focus:border-[#ff4444]"
                        value={search_term}
                        onChange={(e) => { this.setState({ search_term: e.target.value }); }}
                        placeholder={searchPlaceholder}
                        disabled={guildContentLoading}
                    />
                </div>
                {content}
            </div>
        );
    };

    renderIdInput = () => {
        const placeholder = "Enter channel ID";
        const title = "Add Channel by ID";

        return (
            <div className="id-input-container">
                <div className="flex items-center justify-between p-3 border-b border-[#1a1c1f]">
                    {/* Back button */}
                    <button
                        onClick={this.resetSelectionMode} // Go back to default options
                        className="text-[#b9bbbe] hover:text-white mr-2"
                        aria-label="Back to options"
                    >
                        &lt; Back
                    </button>
                    <h3 className="text-lg font-semibold text-[#ffffff] flex-grow text-center" title={title}>{title}</h3>
                    <FontAwesomeIcon
                        icon={faTimes}
                        className="cursor-pointer text-[#b9bbbe] hover:text-white"
                        onClick={() => this.setState({ showDropDown: false })} // Close dropdown entirely
                    />
                </div>
                <div className="p-4 space-y-3">
                    <input
                        type="text"
                        className="w-full bg-[#202225] border border-[#1a1c1f] rounded px-3 py-2 text-[#dcddde] placeholder-[#72767d] focus:outline-none focus:border-[#ff4444]"
                        value={this.state.idToAdd}
                        onChange={(e) => { this.setState({ idToAdd: e.target.value }); }}
                        placeholder={placeholder}
                    />
                    <button
                        className="w-full px-4 py-2 bg-[#ff4444] hover:bg-[#ff6666] text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#ff4444] focus:ring-offset-2 focus:ring-offset-[#2f3136] disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={this.handleChannelAddById}
                        disabled={!this.state.idToAdd.trim()}
                    >
                        Add Channel
                    </button>
                </div>
            </div>
        );
    };

    renderDefaultOptions = () => {
        // Options: Select from Guild, Add by ID, Clear Selection
        return (
            <div className="py-2">
                <div
                    key="selectFromGuild"
                    className="p-3 hover:bg-[#3a3d42] cursor-pointer rounded mx-2 my-1"
                    onClick={this.switchToGuild}
                >
                    <div className="font-medium text-[#ffffff] mb-1">Select from Server</div>
                    <div className="text-sm text-[#b9bbbe]">Choose a server first, then select a channel</div>
                </div>
                <div
                    key="addById"
                    className="p-3 hover:bg-[#3a3d42] cursor-pointer rounded mx-2 my-1 border-t border-[#1a1c1f]"
                    onClick={this.switchToIdInput}
                >
                    <div className="font-medium text-[#ffffff] mb-1">Add by ID</div>
                    <div className="text-sm text-[#b9bbbe]">Directly enter a channel ID</div>
                </div>
                {/* Potentially add "Clear Selection" if needed */}
                {this.state.selectedChannel && (
                    <div
                        key="clearSelection"
                        className="p-3 hover:bg-[#3a3d42] cursor-pointer border-t border-[#1a1c1f] mx-2 my-1 rounded"
                        onClick={() => {
                            this.setState({
                                selectedChannel: null,
                                showDropDown: false,
                                selectionMode: 'default'
                            });
                            this.props.change(null); // Notify parent of cleared selection
                        }}
                    >
                        <div className="font-medium text-[#ffffff] mb-1 text-red-400">Clear Selection</div>
                        <div className="text-sm text-[#b9bbbe]">Remove the currently selected channel</div>
                    </div>
                )}
            </div>
        );
    };

    renderDropDown = () => {
        if (!this.state.showDropDown) return null;

        let content;

        if (this.state.selectionMode === 'default') {
            content = this.renderDefaultOptions();
        } else if (this.state.selectionMode === 'guild') {
            content = this.renderGuildList();
        } else if (this.state.selectionMode === 'guildSelect') {
            content = this.renderGuildContent();
        } else if (this.state.selectionMode === 'idInput') {
            content = this.renderIdInput();
        }
        // No 'idInput' mode for channel select

        return (
            <div className="channel-select-dropdown bg-[#2f3136] rounded-lg shadow-xl absolute z-50 border border-[#1a1c1f] flex flex-col" style={{
                top: "100%", // Position below the button
                marginTop: "4px", // Small gap
                width: "320px",
                maxHeight: "450px",
                overflow: "hidden"
            }}>
                {content}
            </div>
        );
    };


    render() {
        const { selectedChannel } = this.state;
        const isDisabled = this.props.settings?.premium && !this.props.premium;
        const buttonText = selectedChannel ? `#${selectedChannel.name}` : "Select Channel";

        // Apply styles from the provided CSS image
        const buttonClasses = `
            w-full relative
            pl-4 pr-10 // padding-left: 16px; Reduced padding slightly, Adjust pr for icon
            h-[50px] // height: 50px;
            bg-[#36393f] // background-color: #36393f;
            border !border-[#000000] // border: 1px solid #000;
            rounded-[12px] // border-radius: 12px;
            text-base // font-size: 1rem;
            font-medium // font-weight: 500;
            text-left
            appearance-none
            transition-colors duration-200 ease-in-out // transition: border-color ... (applied to all colors by Tailwind)
            ${isDisabled
                ? 'opacity-50 cursor-not-allowed'
                // Add hover/focus styles if desired, image doesn't show them but Select.js had hover:border-[#ff4444] focus:border-[#ff4444]
                : 'hover:border-[#4f545c] hover:cursor-pointer focus:outline-none focus:border-[#4f545c]'
            }
        `;
        // Text color from image, placeholder uses a slightly dimmer white/grey
        const textClasses = selectedChannel ? "text-[#ffffff]" : "text-[#b9bbbe]";

        return (
            <div className={`${isDisabled ? "opacity-75 pointer-events-none" : ""} ${!this.props.slot ? "section-content-normal" : "mb-15"}`}>
                <div className="relative"> {/* Added relative positioning for dropdown */}
                    <InputTitle settings={this.props.settings} refresh={true} />
                    <div className="section-content-header mb-2">
                        {this.renderDescription()}
                    </div>
                    <div className="mt-1 relative"> {/* Wrapper for button + icon + dropdown - flex items-center for vertical centering */}
                        <button
                            type="button"
                            className={buttonClasses}
                            onClick={() => {
                                if (!isDisabled) {
                                    this.setState(prevState => ({
                                        showDropDown: !prevState.showDropDown,
                                        selectionMode: 'default'
                                    }));
                                }
                            }}
                            disabled={isDisabled}
                        >
                            {/* Ensure text is vertically centered if button height causes issues */}
                            <span className={`truncate ${textClasses}`}>{buttonText}</span>
                            {/* Icon positioning from CSS image is default select, mimic Select.js positioning */}
                            <div className='absolute right-5 top-1/2 transform -translate-y-1/2 pointer-events-none'>
                                <FontAwesomeIcon icon={faChevronDown} className={textClasses} />
                            </div>
                        </button>
                        {this.renderDropDown()}
                    </div>
                </div>
            </div>
        );
    }
}

// Add scrollbar styles if not already present globally
const styleId = 'custom-scrollbar-styles';
if (!document.getElementById(styleId)) {
    const styleSheet = document.createElement("style");
    styleSheet.id = styleId;
    styleSheet.type = "text/css";
    styleSheet.innerText = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 8px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #2f3136; /* Darker background for track */
      border-radius: 4px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background-color: #4f545c; /* Slightly lighter thumb */
      border-radius: 4px;
      border: 2px solid #2f3136; /* Creates padding around thumb */
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background-color: #72767d; /* Lighter on hover */
    }
    `;
    document.head.appendChild(styleSheet);
}


const mapStateToProps = (state) => ({
    bot: state.data.bot, // Need bot ID for API calls
    // Keep other state mappings if they were used (e.g., premium status)
    premium: state.data.premium // Assuming this comes from redux state based on original code
});

// mapDispatchToProps remains empty unless actions are needed
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ChannelSelect);